import { useEffect, useRef, useState, useMemo } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { MARKETING } from "../theme/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useLockPageScroll } from "../utils/useLockPageScroll";
import { storeReferral } from "../utils/referralLink";

/* Marketing landing page, ported from the scoutm8-landing design export. */

const LOGO = "/assets/scoutm8-landing-logo.png";

/* Palette lives in theme/ThemeContext (MARKETING) — this page pins one fixed
   look rather than following the app theme, but the values are defined once. */
const C = MARKETING;

const SERIF = "'Instrument Serif', Georgia, serif";
const SANS = "'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO = "'Geist Mono', ui-monospace, SFMono-Regular, monospace";


const num = (n) => Number(n || 0).toLocaleString("en-US");

const LandingGlobalStyle = createGlobalStyle`
  html {
    background: ${C.onDark} !important;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }
  body {
    margin: 0;
    padding: 0;
    background: ${C.onDark} !important;
    font-family: ${SANS};
    font-size: 15px;
    color: ${C.ink};
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
  }
  @keyframes sm8blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
  @keyframes sm8pulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
  @keyframes sm8glow {
    0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, ${C.orange} 0%, transparent); }
    45% { box-shadow: 0 0 0 7px color-mix(in srgb, ${C.orange} 16%, transparent); }
  }
  /* Ripple that expands out of the pointer tip on each simulated click */
  @keyframes sm8ripple {
    0% { transform: scale(.2); opacity: .8; }
    60% { opacity: .28; }
    100% { transform: scale(1.5); opacity: 0; }
  }
  /* Pointer dips as it presses, which is what actually reads as a click */
  @keyframes sm8press {
    0%, 100% { transform: scale(1); }
    35% { transform: scale(.8); }
  }
`;

const Page = styled.div`
  background: ${C.onDark};
  font-family: ${SANS};
  color: ${C.ink};

  /* Sticky nav would otherwise cover the top of every anchored section */
  section[id],
  header[id],
  div[id] {
    scroll-margin-top: 90px;
  }
`;

const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 44px;

  @media (max-width: 900px) {
    padding: 0 28px;
  }
  @media (max-width: 560px) {
    padding: 0 20px;
  }
`;

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: color-mix(in srgb, ${C.onDark} 86%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${C.hair};
`;

const NavInner = styled(Container)`
  height: 74px;
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 560px) {
    height: 64px;
    gap: 16px;
  }
`;

/* Logo link in the nav — never shrinks, so the wordmark keeps its aspect ratio */
const NavLogo = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 30px;
    width: auto;
    display: block;
  }
  @media (max-width: 560px) {
    img {
      height: 24px;
    }
  }
`;

/* Right-hand nav buttons. Below 960px a menu button takes over the page links;
   below 640px Log in and Get a demo move into that menu too */
const NavActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  [data-nav-demo] {
    height: 40px;
    padding: 0 18px;
    font-size: 14px;
  }
  @media (max-width: 640px) {
    gap: 8px;
    [data-nav-login],
    [data-nav-demo] {
      display: none;
    }
    /* Beat the app-wide 44px touch-target floor, which makes the bar chunky */
    [data-nav-signup] {
      height: 38px;
      min-height: 38px;
      padding: 0 15px;
      font-size: 13.5px;
    }
  }
  @media (max-width: 360px) {
    [data-nav-extra] {
      display: none;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  min-height: 40px;
  flex-shrink: 0;
  border: 1px solid ${C.border};
  border-radius: 999px;
  background: ${C.onDark};
  color: ${C.ink};
  cursor: pointer;
  place-items: center;
  padding: 0;
  transition: background 0.18s ease;

  &:hover {
    background: ${C.wash};
  }
  @media (max-width: 960px) {
    display: grid;
  }
  @media (max-width: 640px) {
    width: 38px;
    height: 38px;
    min-height: 38px;
  }
`;

/* Drops down under the sticky nav, full width */
const MobileMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${C.onDark};
  border-bottom: 1px solid ${C.hair};
  box-shadow: 0 24px 40px -24px color-mix(in srgb, ${C.deep} 28%, transparent);
  padding: 8px 20px 20px;

  @media (min-width: 961px) {
    display: none;
  }

  a {
    display: flex;
    align-items: center;
    min-height: 48px;
    font-size: 16px;
    color: ${C.ink};
    text-decoration: none;
    border-bottom: 1px solid ${C.hair};
  }
  a[aria-current="page"] {
    color: ${C.blue};
  }

  /* Log in + Get a demo only live here once the bar has dropped them */
  [data-menu-actions] {
    display: none;
    gap: 10px;
    margin-top: 18px;
  }
  [data-menu-actions] > * {
    flex: 1;
    height: 44px;
  }
  @media (max-width: 640px) {
    [data-menu-actions] {
      display: flex;
    }
  }
`;

const LANDING_LINKS = [
  { href: "#how", label: "How It Works" },
  { href: "#features", label: "Product" },
  { href: "#copilot", label: "AI Co-pilot" },
  { href: "#stats", label: "At a glance" },
  { href: "/pricing", label: "Pricing" },
];

const IconMenu = (p) => (
  <Svg {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
);

const NavLinks = styled.div`
  display: flex;
  gap: 30px;

  @media (max-width: 960px) {
    display: none;
  }
`;

const NavLink = styled.a`
  font-size: 14px;
  color: ${C.body};
  white-space: nowrap;
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: ${C.blue};
  }
`;

const TextButton = styled.button`
  font-family: inherit;
  font-size: 14px;
  color: ${C.ink};
  background: none;
  border: 0;
  padding: 0 14px;
  cursor: pointer;
  transition: color 0.18s ease;

  &:hover {
    color: ${C.blue};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  height: ${({ $lg }) => ($lg ? "48px" : "40px")};
  padding: ${({ $lg }) => ($lg ? "0 26px" : "0 18px")};
  border: 0;
  border-radius: 999px;
  background: ${C.orange};
  color: ${C.onDark};
  font-size: ${({ $lg }) => ($lg ? "15px" : "14px")};
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: ${({ $onDark }) => ($onDark ? C.orangeBright : C.orangeDark)};
  }
  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

const GhostLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  border-radius: 999px;
  background: ${C.onDark};
  color: ${C.ink};
  border: 1px solid ${C.border};
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.18s ease,
    color 0.18s ease;

  &:hover {
    background: ${C.wash};
    color: ${C.blue};
  }
`;

const OutlineDarkLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  border-radius: 999px;
  background: transparent;
  color: ${C.onDark};
  border: 1px solid color-mix(in srgb, ${C.onDark} 30%, transparent);
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: color-mix(in srgb, ${C.onDark} 10%, transparent);
    color: ${C.onDark};
  }
`;

const Section = styled.section`
  padding: 118px 0;

  @media (max-width: 900px) {
    padding: 80px 0;
  }
  @media (max-width: 560px) {
    padding: 64px 0;
  }
`;

const Serif = styled.em`
  font-family: ${SERIF};
  font-weight: 400;
  font-style: italic;
  letter-spacing: -0.01em;
`;

const H1 = styled.h1`
  font-family: ${SANS};
  font-weight: 600;
  font-size: clamp(38px, 8vw, 70px);
  line-height: 1.02;
  letter-spacing: -0.033em;
  margin: 0 auto;
  max-width: 900px;
  color: ${C.deep};
  text-wrap: balance;
`;

const H2 = styled.h2`
  font-family: ${SANS};
  font-weight: 600;
  font-size: clamp(30px, 5.4vw, 46px);
  line-height: 1.07;
  letter-spacing: -0.024em;
  color: ${C.deep};
  margin: 0;
  text-wrap: balance;
`;

const H3 = styled.h3`
  font-family: ${SANS};
  font-weight: 600;
  font-size: clamp(25px, 3.6vw, 33px);
  line-height: 1.12;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${C.deep};
`;

const Eyebrow = styled.div`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${C.blue};
  margin-bottom: 14px;
`;

const SectionHead = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto ${({ $tight }) => ($tight ? "56px" : "68px")};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 76px;
  align-items: center;
  margin-top: ${({ $spaced }) => ($spaced ? "124px" : "0")};

  @media (max-width: 1000px) {
    gap: 48px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 36px;
    margin-top: ${({ $spaced }) => ($spaced ? "72px" : "0")};

    /* Stack copy above its illustration, whichever side it sits on */
    & > * {
      order: 0 !important;
    }
    & > [data-copy] {
      order: -1 !important;
    }
  }
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  max-width: 940px;
  margin: 0 auto;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }
  /* Cards carry inline desktop padding; 32px sides inside the page gutter squeeze the lists */
  @media (max-width: 560px) {
    & > * {
      padding: 26px 22px !important;
    }
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  text-align: center;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 24px;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }
  /* Half a phone is too narrow for the address block, so the brand column takes its own row */
  @media (max-width: 560px) {
    gap: 32px 24px;
    & > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: 52px;
  padding-top: 22px;
  border-top: 1px solid ${C.hair};
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  color: ${C.muted};

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

/* Renders as an <a> for links and as a <button> for the demo form trigger */
const FooterLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  align-self: flex-start;
  font-family: inherit;
  font-size: 14px;
  color: ${C.body};
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: ${C.blue};
  }

  /* The app-wide 44px touch target turns the link lists into tall gappy columns */
  @media (max-width: 450px) {
    min-height: 0;
    padding: 4px 0;
  }
`;

/* Address / phone rows in the footer brand column */
const ContactLine = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${C.muted};

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.18s ease;
  }
  a:hover {
    color: ${C.blue};
  }
`;

const HeroWrap = styled.header`
  position: relative;
  overflow: hidden;
  text-align: center;
  /* Bottom padding keeps the demo card off the next section and lets its
     drop shadow finish inside the hero's overflow clip */
  padding: clamp(56px, 8vw, 92px) 0 clamp(72px, 9vw, 118px);
`;

const HeroCTAs = styled.div`
  margin-top: 34px;
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const DemoCard = styled.div`
  max-width: 1000px;
  margin: 62px auto 0;
  text-align: left;
  background: ${C.onDark};
  border: 1px solid ${C.line};
  border-radius: 20px;
  box-shadow:
    0 44px 96px -38px color-mix(in srgb, ${C.deep} 34%, transparent),
    0 14px 30px -16px color-mix(in srgb, ${C.deep} 12%, transparent);
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    margin: 46px 20px 0;
  }
`;

const DemoStage = styled.div`
  position: relative;
  height: 426px;
  padding: 30px 34px;

  @media (max-width: 700px) {
    height: auto;
    min-height: 360px;
    padding: 22px 18px;
  }
`;

/* Simulated pointer that walks the demo — decorative, never on touch layouts */
const CursorLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;

  @media (max-width: 700px) {
    display: none;
  }
`;

const CursorBody = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  transition:
    transform 0.38s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.22s ease;
  will-change: transform;

  /* Remounted per click so the press animation restarts every time */
  svg {
    position: absolute;
    left: -2px;
    top: -2px;
    transform-origin: 3px 3px;
    filter: drop-shadow(0 3px 6px color-mix(in srgb, ${C.deep} 28%, transparent));
    animation: sm8press 0.34s cubic-bezier(0.3, 0, 0.2, 1);
  }
`;

/* Two staggered rings — the trailing one gives the ripple its depth.
   Every click target is a solid orange button, so the orange stroke is wrapped
   in a white halo on both sides to stay legible on orange and on white alike */
const ClickRing = styled.span`
  position: absolute;
  left: ${({ $size }) => -$size / 2}px;
  top: ${({ $size }) => -$size / 2}px;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 99px;
  border: 2px solid ${C.orange};
  box-shadow:
    0 0 0 1.6px color-mix(in srgb, ${C.onDark} 92%, transparent),
    inset 0 0 0 1.6px color-mix(in srgb, ${C.onDark} 92%, transparent);
  background: ${({ $fill }) =>
    $fill ? `color-mix(in srgb, ${C.onDark} 30%, transparent)` : "transparent"};
  opacity: 0;
  animation: sm8ripple ${({ $dur }) => $dur}s cubic-bezier(0.2, 0.6, 0.3, 1)
    ${({ $delay }) => $delay}s forwards;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  height: 54px;
  border: 1px solid ${C.border};
  border-radius: 14px;
  padding: 0 16px;
  font-size: 15.5px;
  color: ${C.ink};
  box-shadow: 0 1px 2px color-mix(in srgb, ${C.deep} 5%, transparent);
  overflow: hidden;

  /* Leaves room for the typed company name beside the button on narrow phones */
  @media (max-width: 560px) {
    gap: 8px;
    padding: 0 12px;
  }
`;

const Dot = styled.button`
  height: 7px;
  /* Beat the app-wide 44px min touch target, which would balloon the dots */
  min-height: 7px;
  border: 0;
  padding: 0;
  border-radius: 99px;
  cursor: pointer;
  transition: all 0.25s;
  width: ${({ $on }) => ($on ? "22px" : "7px")};
  background: ${({ $on }) => ($on ? C.orange : C.border)};
`;

const IllustrationCard = styled.div`
  background: ${C.onDark};
  border: 1px solid ${C.line};
  border-radius: 20px;
  box-shadow: 0 24px 56px -28px color-mix(in srgb, ${C.deep} 22%, transparent);
  padding: 26px;

  @media (max-width: 560px) {
    padding: 20px;
  }
`;

const FaqButton = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  cursor: pointer;
  font-family: ${SANS};
  font-size: 18px;
  font-weight: 500;
  color: ${C.ink};
  padding: 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  transition: color 0.18s ease;

  &:hover {
    color: ${C.blue};
  }
`;

const CTAPanel = styled.div`
  margin: 110px auto;
  max-width: 1060px;
  background: ${C.deep};
  border-radius: 30px;
  text-align: center;
  padding: 88px 40px;
  position: relative;
  overflow: hidden;

  @media (max-width: 700px) {
    margin: 72px auto;
    padding: 64px 24px;
    border-radius: 24px;
  }
`;

const CTAButtons = styled.div`
  position: relative;
  margin-top: 34px;
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const StatValue = styled.div`
  font-family: ${({ $serif }) => ($serif ? SERIF : SANS)};
  font-weight: ${({ $serif }) => ($serif ? 400 : 600)};
  font-style: ${({ $serif }) => ($serif ? "italic" : "normal")};
  font-size: clamp(36px, 5.2vw, ${({ $serif }) => ($serif ? "58px" : "54px")});
  letter-spacing: -0.03em;
  color: ${({ $serif }) => ($serif ? C.orange : C.deep)};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const Quote = styled.p`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(24px, 4.4vw, 39px);
  line-height: 1.32;
  letter-spacing: -0.008em;
  color: ${C.deep};
  margin: 0;
  text-wrap: pretty;
`;

/* --- icons (inlined from the export) --- */

const Svg = ({ size = 22, stroke = "currentColor", width, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={width || 1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children}
  </svg>
);

const IconPin = (p) => (
  <Svg {...p}>
    <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);
const IconMail = (p) => (
  <Svg {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </Svg>
);
const IconDoc = (p) => (
  <Svg {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="m9 15 2 2 4-4" />
  </Svg>
);
const IconBot = (p) => (
  <Svg {...p}>
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M2 14h2" />
    <path d="M20 14h2" />
    <path d="M15 13v2" />
    <path d="M9 13v2" />
  </Svg>
);
const IconConnections = (p) => (
  <Svg {...p}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);
const IconCheck = (p) => (
  <Svg {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Svg>
);
const IconCross = (p) => (
  <Svg {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);
const IconPlus = (p) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
const IconUser = (p) => (
  <Svg {...p}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </Svg>
);
const IconChevronDown = (p) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);
const IconUserSearch = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="7" r="4" />
    <path d="M10.3 15H7a4 4 0 0 0-4 4v2" />
    <circle cx="17" cy="17" r="3" />
    <path d="m21 21-1.9-1.9" />
  </Svg>
);
const IconBuildingPlus = (p) => (
  <Svg {...p}>
    <path d="M3 21h11" />
    <path d="M5 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v6" />
    <path d="M8 7h1M8 11h1M8 15h1" />
    <path d="M18 13v7M14.5 16.5h7" />
  </Svg>
);
const IconPhone = (p) => (
  <Svg {...p}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
  </Svg>
);
const IconLinkedIn = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3zM9 9h3.8v1.71h.05a4.17 4.17 0 0 1 3.75-2.06c4.01 0 4.75 2.64 4.75 6.07V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.07 1.4-2.07 2.85V21H9z" />
  </Svg>
);
const IconYouTube = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M21.58 7.19a2.51 2.51 0 0 0-1.77-1.77C18.25 5 12 5 12 5s-6.25 0-7.81.42a2.51 2.51 0 0 0-1.77 1.77A26.1 26.1 0 0 0 2 12a26.1 26.1 0 0 0 .42 4.81 2.51 2.51 0 0 0 1.77 1.77C5.75 19 12 19 12 19s6.25 0 7.81-.42a2.51 2.51 0 0 0 1.77-1.77A26.1 26.1 0 0 0 22 12a26.1 26.1 0 0 0-.42-4.81ZM10 15.02V8.98L15.2 12Z" />
  </Svg>
);

/* Feature bullet with the orange tick used across the feature rows */
const Bullet = ({ children }) => (
  <div
    style={{
      display: "flex",
      gap: 11,
      alignItems: "flex-start",
      fontSize: "14.5px",
      color: C.bodyStrong,
    }}
  >
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: 99,
        flexShrink: 0,
        marginTop: 1,
        background: C.orangeWash,
        color: C.orange,
        display: "grid",
        placeItems: "center",
      }}
    >
      <IconCheck size={11} width={3} />
    </span>
    {children}
  </div>
);

const FeatureIcon = ({ tone, children }) => (
  <div
    style={{
      width: 46,
      height: 46,
      borderRadius: 13,
      background: tone === "orange" ? C.orangeWash : C.blueWash,
      color: tone === "orange" ? C.orange : C.blue,
      display: "grid",
      placeItems: "center",
      marginBottom: 20,
    }}
  >
    {children}
  </div>
);

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = (e) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

/* Fades a block in the first time it scrolls into view */
function Reveal({ children, style, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    if (reduce || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(20px)",
        transition:
          "opacity .7s cubic-bezier(.2,.7,.3,1), transform .7s cubic-bezier(.2,.7,.3,1)",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* Counts up to `value` once visible; renders the final value when motion is reduced */
function CountUp({ value, comma = false, suffix = "", serif = false }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(
    () => (comma ? num(value) : String(value)) + suffix,
  );
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    const format = (v) => (comma ? num(v) : String(v)) + suffix;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setDisplay(format(value));
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;

    let frame = null;
    let start = null;
    const dur = 1300;
    const run = (t) => {
      if (start === null) start = t;
      const p = Math.min((t - start) / dur, 1);
      setDisplay(format(Math.round(value * (1 - Math.pow(1 - p, 3)))));
      if (p < 1) frame = requestAnimationFrame(run);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            frame = requestAnimationFrame(run);
            io.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [value, comma, suffix, reduce]);

  return (
    <StatValue ref={ref} $serif={serif}>
      {display}
    </StatValue>
  );
}

/* The hero walkthrough: one recruiter's flow from tracking a firm to a placement */
const CAPS = [
  ["01", "Add any company you want tracked"],
  ["02", "Their live roles land in your feed"],
  ["03", "Pick the roles worth working"],
  ["04", "Ranked candidates, in minutes"],
  ["05", "AI drafts the outreach"],
  ["06", "Track replies through to placement"],
];
/* Each slide ends just after its last click lands — idle tail time makes the
   demo read as a screenshot, and people start clicking the mockup */
const DURATIONS = [2500, 2100, 3000, 2300, 3800, 2500];

/* Matches the CursorBody transform transition; clicks fire once the pointer lands */
const CURSOR_MOVE_MS = 380;
const DEMO_COMPANY = "Stone King";
const DEMO_EMAIL =
  "Hi Jonathan — Stone King is building out its restructuring team in London.\nGiven your work at Allen & Gledhill, it looks like a strong fit — worth a short call this week?";

const DEMO_ROLES = [
  ["Restructuring Associate", "London · posted 2h ago", true],
  ["Senior Associate, Banking", "London · posted 6h ago", true],
  ["Counsel, Financial Services", "Bristol · posted 1d ago", false],
];

const DEMO_CANDIDATES = [
  ["JT", C.blueWash, C.blue, "Jonathan Tan", "Associate · Allen & Gledhill", "95%"],
  ["PS", C.orangeWash, C.orange, "Priya Sharma", "Senior Associate · Rajah & Tann", "89%"],
  ["MK", C.blueWash, C.blue, "Marcus Koh", "Associate · Withers", "84%"],
];

const DEMO_PIPELINE = [
  ["JT", "Jonathan Tan", "Replied", C.blue, C.blueWash],
  ["PS", "Priya Sharma", "Contacted", C.muted, C.rule],
  ["MK", "Marcus Koh", "Submitted", C.orange, C.orangeWash],
];

/* Panel heading inside a demo slide */
const DemoHead = ({ children, action }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      fontSize: "13.5px",
      color: C.body,
    }}
  >
    {children}
    {action && (
      <span
        style={{
          marginLeft: "auto",
          fontFamily: MONO,
          fontSize: "11.5px",
          color: C.muted,
          whiteSpace: "nowrap",
        }}
      >
        {action}
      </span>
    )}
  </div>
);

const Avatar = ({ initials, bg, fg, size = 38 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: 99,
      background: bg,
      color: fg,
      fontWeight: 600,
      fontSize: 14,
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
    }}
  >
    {initials}
  </span>
);

function HeroDemo() {
  const [slide, setSlide] = useState(0);
  const [typedCompany, setTypedCompany] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [cursor, setCursor] = useState({ x: 0, y: 0, shown: false, click: 0 });
  const stageRef = useRef(null);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    if (reduce) {
      setTypedCompany(DEMO_COMPANY);
      setTypedEmail(DEMO_EMAIL);
      return undefined;
    }
    const timers = [];
    setTypedCompany("");
    setTypedEmail("");

    const type = (setter, text, speed, delay = 0) => {
      let n = 0;
      const step = () => {
        setter(text.slice(0, n));
        n += 1;
        if (n <= text.length) timers.push(setTimeout(step, speed));
      };
      timers.push(setTimeout(step, delay));
    };

    if (slide === 0) type(setTypedCompany, DEMO_COMPANY, 52);
    if (slide === 4) type(setTypedEmail, DEMO_EMAIL, 11, 150);
    return () => timers.forEach(clearTimeout);
  }, [slide, reduce]);

  /* Runs through on hover too — freezing under the pointer is what made people
     read the card as a screenshot and start clicking it */
  useEffect(() => {
    if (reduce) return undefined;
    const t = setTimeout(
      () => setSlide((prev) => (prev + 1) % CAPS.length),
      DURATIONS[slide],
    );
    return () => clearTimeout(t);
  }, [slide, reduce]);

  /* Walk the pointer between the [data-cursor] targets the active slide marks.
     Positions are measured on arrival, so typing that reflows a slide is fine. */
  useEffect(() => {
    if (reduce) {
      setCursor((c) => ({ ...c, shown: false }));
      return undefined;
    }
    const stage = stageRef.current;
    if (!stage) return undefined;
    const targets = Array.from(stage.querySelectorAll("[data-cursor]")).sort(
      (a, b) => Number(a.dataset.cursor) - Number(b.dataset.cursor),
    );
    if (!targets.length) {
      setCursor((c) => ({ ...c, shown: false }));
      return undefined;
    }

    const timers = [];
    targets.forEach((el) => {
      const arriveAt = Number(el.dataset.cursor);
      const hoverOnly = el.dataset.cursorHover === "1";
      timers.push(
        setTimeout(() => {
          const box = stage.getBoundingClientRect();
          const r = el.getBoundingClientRect();
          setCursor((c) => ({
            ...c,
            x: r.left - box.left + r.width / 2,
            y: r.top - box.top + r.height / 2,
            shown: true,
          }));
        }, arriveAt),
      );
      if (!hoverOnly) {
        timers.push(
          setTimeout(
            () => setCursor((c) => ({ ...c, click: c.click + 1 })),
            arriveAt + CURSOR_MOVE_MS + 60,
          ),
        );
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [slide, reduce]);

  return (
    <DemoCard>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "13px 20px",
          borderBottom: `1px solid ${C.hair}`,
          background: C.wash,
        }}
      >
        <img
          src={LOGO}
          alt="scoutm8"
          style={{ height: 19, width: "auto", display: "block" }}
        />
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: C.blue,
            background: C.blueWash,
            padding: "4px 11px",
            borderRadius: 999,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          <i
            style={{
              width: 6,
              height: 6,
              borderRadius: 99,
              background: C.blue,
              animation: "sm8pulse 1.5s ease infinite",
              display: "block",
            }}
          />
          Live demo
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          {CAPS.map((cap, n) => (
            <Dot
              key={cap[0]}
              type="button"
              aria-label={`Show step ${cap[0]}: ${cap[1]}`}
              $on={slide === n}
              onClick={() => setSlide(n)}
            />
          ))}
        </div>
      </div>

      <DemoStage ref={stageRef}>
        <CursorLayer aria-hidden="true">
          <CursorBody
            style={{
              transform: `translate(${cursor.x}px, ${cursor.y}px)`,
              opacity: cursor.shown ? 1 : 0,
            }}
          >
            {cursor.click > 0 && (
              <>
                <ClickRing key={`a${cursor.click}`} $size={40} $dur={0.55} $delay={0} $fill />
                <ClickRing key={`b${cursor.click}`} $size={40} $dur={0.55} $delay={0.12} />
              </>
            )}
            <svg key={cursor.click} width="20" height="22" viewBox="0 0 20 22" fill="none">
              <path
                d="M2 1.6 16.6 12.1l-6.2 1 3.2 6.2-2.5 1.3-3.2-6.2-4.5 3.6z"
                fill={C.deep}
                stroke={C.onDark}
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
          </CursorBody>
        </CursorLayer>

        {/* 01 — track a company the platform doesn't cover yet */}
        {slide === 0 && (
          <div>
            <SearchBar>
              <IconBuildingPlus size={17} stroke={C.muted} style={{ flexShrink: 0 }} />
              <span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                {typedCompany}
              </span>
              <span
                style={{
                  width: 2,
                  height: 21,
                  background: C.orange,
                  animation: "sm8blink 1s step-end infinite",
                  flexShrink: 0,
                }}
              />
              <span
                data-cursor="850"
                style={{
                  marginLeft: "auto",
                  background: C.orange,
                  color: C.onDark,
                  borderRadius: 10,
                  padding: "9px 17px",
                  fontSize: "13.5px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Track company
              </span>
            </SearchBar>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {[
                ["stoneking.co.uk/careers", "Careers page found · Workday", "Tracking", C.blue, C.blueWash],
                ["Scanning 34 open roles…", "First sync runs in seconds", "Scouting", C.orange, C.orangeWash],
              ].map(([title, meta, badge, fg, bg]) => (
                <div
                  key={title}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "13px 16px",
                    border: `1px solid ${C.line}`,
                    borderRadius: 13,
                    background: C.onDark,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: 600,
                        color: C.ink,
                        // The careers URL has no break points and runs under the badge on phones
                        overflowWrap: "anywhere",
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontFamily: MONO,
                        fontSize: "11.5px",
                        color: C.muted,
                        marginTop: 3,
                      }}
                    >
                      {meta}
                    </div>
                  </div>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: fg,
                      background: bg,
                      borderRadius: 999,
                      padding: "5px 11px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {badge}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 02 — the company's live roles arrive in the feed */}
        {slide === 1 && (
          <div>
            <DemoHead action="Synced just now">
              <b style={{ color: C.ink, fontWeight: 600 }}>Stone King</b> · 34
              live roles
            </DemoHead>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              {DEMO_ROLES.map(([title, meta, isNew], i) => (
                <div
                  key={title}
                  data-cursor={i === 0 ? "350" : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "13px 16px",
                    border: `1px solid ${C.line}`,
                    borderRadius: 13,
                    background: C.onDark,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: 600,
                        color: C.ink,
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: C.muted,
                        marginTop: 2,
                      }}
                    >
                      {meta}
                    </div>
                  </div>
                  {isNew && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: C.orange,
                        background: C.orangeWash,
                        borderRadius: 999,
                        padding: "5px 11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      New
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 03 — the recruiter picks a role to work */}
        {slide === 2 && (
          <div>
            <DemoHead action="1 selected">Choose the roles to work</DemoHead>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              {DEMO_ROLES.map(([title, meta], i) => {
                const picked = i === 0;
                return (
                  <div
                    key={title}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 13,
                      padding: "13px 16px",
                      border: `1px solid ${picked ? C.orange : C.line}`,
                      borderRadius: 13,
                      background: picked ? C.orangeWash : C.onDark,
                    }}
                  >
                    <span
                      data-cursor={picked ? "400" : undefined}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        flexShrink: 0,
                        border: `1.5px solid ${picked ? C.orange : C.border}`,
                        background: picked ? C.orange : C.onDark,
                        color: C.onDark,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {picked && <IconCheck size={12} width={3} />}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "14.5px",
                          fontWeight: 600,
                          color: C.ink,
                        }}
                      >
                        {title}
                      </div>
                      <div
                        style={{
                          fontSize: "12.5px",
                          color: C.muted,
                          marginTop: 2,
                        }}
                      >
                        {meta}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                marginTop: 14,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <span
                data-cursor="1500"
                style={{
                  background: C.orange,
                  color: C.onDark,
                  borderRadius: 11,
                  padding: "11px 18px",
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  animation: "sm8glow 1.3s ease infinite",
                }}
              >
                ✦ Find candidates
              </span>
            </div>
          </div>
        )}

        {/* 04 — ranked candidates come back for that role */}
        {slide === 3 && (
          <div>
            <DemoHead action="24 profiles scanned">
              <b style={{ color: C.ink, fontWeight: 600 }}>
                Restructuring Associate
              </b>{" "}
              · ranked by fit
            </DemoHead>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              {DEMO_CANDIDATES.map(
                ([initials, bg, fg, name, role, score], i) => (
                <div
                  key={name}
                  data-cursor={i === 0 ? "450" : undefined}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "38px 1fr auto",
                    gap: 13,
                    alignItems: "center",
                    padding: "12px 16px",
                    border: `1px solid ${C.line}`,
                    borderRadius: 13,
                    background: C.onDark,
                  }}
                >
                  <Avatar initials={initials} bg={bg} fg={fg} />
                  <div>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: 600,
                        color: C.ink,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: C.muted,
                        marginTop: 2,
                      }}
                    >
                      {role}
                    </div>
                  </div>
                  <span
                    style={{
                      fontFamily: MONO,
                      fontSize: "12.5px",
                      color: C.blue,
                      background: C.blueWash,
                      borderRadius: 999,
                      padding: "5px 12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {score} fit
                  </span>
                </div>
                ),
              )}
            </div>
          </div>
        )}

        {/* 05 — AI writes the outreach to the candidate */}
        {slide === 4 && (
          <div
            style={{
              border: `1px solid ${C.line}`,
              borderRadius: 15,
              overflow: "hidden",
              maxWidth: 620,
              boxShadow: `0 1px 2px color-mix(in srgb, ${C.deep} 5%, transparent)`,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 9,
                padding: "12px 17px",
                borderBottom: `1px solid ${C.hair}`,
                fontSize: "13.5px",
                color: C.body,
              }}
            >
              <b style={{ color: C.ink, fontWeight: 600 }}>To</b> Jonathan Tan ·
              Associate, Allen &amp; Gledhill
            </div>
            <div
              style={{
                display: "flex",
                gap: 9,
                padding: "12px 17px",
                borderBottom: `1px solid ${C.hair}`,
                fontSize: "13.5px",
                color: C.body,
              }}
            >
              <b style={{ color: C.ink, fontWeight: 600 }}>Subject</b> A
              restructuring role at Stone King
            </div>
            <div
              style={{
                padding: "16px 17px",
                fontSize: 14,
                lineHeight: 1.65,
                color: C.bodyStrong,
                whiteSpace: "pre-line",
                minHeight: 116,
              }}
            >
              {typedEmail}
              <span
                style={{
                  display: "inline-block",
                  verticalAlign: -3,
                  width: 2,
                  height: 17,
                  background: C.orange,
                  animation: "sm8blink 1s step-end infinite",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "12px 15px",
                borderTop: `1px solid ${C.hair}`,
                background: C.wash,
              }}
            >
              {/* Allowed to wrap: unbroken, it pushes Send out of the clipped box on phones */}
              <span
                style={{
                  fontSize: "12.5px",
                  color: C.blue,
                }}
              >
                ✦ AI drafted · matched to the role
              </span>
              <span
                data-cursor="2200"
                style={{
                  background: C.orange,
                  color: C.onDark,
                  borderRadius: 10,
                  padding: "9px 17px",
                  fontSize: "13.5px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  animation: "sm8glow 1.3s ease infinite",
                }}
              >
                Send
              </span>
            </div>
          </div>
        )}

        {/* 06 — statuses move as candidates reply */}
        {slide === 5 && (
          <div>
            <DemoHead action="Updated 1m ago">
              <b style={{ color: C.ink, fontWeight: 600 }}>Pipeline</b> · Stone
              King, Restructuring Associate
            </DemoHead>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              {DEMO_PIPELINE.map(([initials, name, status, fg, bg], i) => (
                <div
                  key={name}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "38px 1fr auto",
                    gap: 13,
                    alignItems: "center",
                    padding: "12px 16px",
                    border: `1px solid ${i === 0 ? C.blue : C.line}`,
                    borderRadius: 13,
                    background: C.onDark,
                  }}
                >
                  <Avatar
                    initials={initials}
                    bg={i === 2 ? C.orangeWash : C.blueWash}
                    fg={i === 2 ? C.orange : C.blue}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: 600,
                        color: C.ink,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: C.muted,
                        marginTop: 2,
                      }}
                    >
                      {i === 0
                        ? "Replied 4m ago · wants a call Thursday"
                        : i === 1
                          ? "Outreach sent · awaiting reply"
                          : "Sent to the hiring manager"}
                    </div>
                  </div>
                  <span
                    data-cursor={i === 0 ? "450" : undefined}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: fg,
                      background: bg,
                      borderRadius: 999,
                      padding: "6px 12px",
                      whiteSpace: "nowrap",
                      animation:
                        i === 0 ? "sm8glow 1.3s ease infinite" : undefined,
                    }}
                  >
                    {status}
                    <IconChevronDown size={13} stroke={fg} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DemoStage>

      <div
        style={{
          padding: "13px 20px",
          borderTop: `1px solid ${C.hair}`,
          background: C.wash,
          fontFamily: MONO,
          fontSize: "12.5px",
          letterSpacing: ".02em",
          color: C.muted,
        }}
      >
        <b style={{ color: C.blue, fontWeight: 500 }}>{CAPS[slide][0]}</b> ·{" "}
        {CAPS[slide][1]}
      </div>
    </DemoCard>
  );
}

/* ------------------------------------------------------------------ */
/* Feature illustrations — static mockups that sit beside each feature  */
/* ------------------------------------------------------------------ */

const Stack = ({ gap = 12, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>{children}</div>
);

/* Input-looking row with a leading icon and a trailing action button */
const MockField = ({ icon, action, actionTone = C.blue, children }) => (
  <div
    style={{
      minHeight: 50,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 15px",
      fontSize: "14.5px",
      color: C.ink,
      boxShadow: `0 1px 2px color-mix(in srgb, ${C.deep} 5%, transparent)`,
    }}
  >
    {icon}
    {children}
    {action && (
      <span
        style={{
          marginLeft: "auto",
          fontSize: "12.5px",
          color: C.onDark,
          background: actionTone,
          borderRadius: 8,
          padding: "6px 12px",
          whiteSpace: "nowrap",
        }}
      >
        {action}
      </span>
    )}
  </div>
);

const MockRow = ({
  avatar,
  title,
  meta,
  monoMeta,
  badge,
  badgeFg = C.blue,
  badgeBg = C.blueWash,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      border: `1px solid ${C.line}`,
      borderRadius: 12,
      background: C.wash,
    }}
  >
    {avatar}
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: C.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: monoMeta ? MONO : undefined,
          fontSize: monoMeta ? "11.5px" : 12,
          color: C.muted,
          marginTop: 3,
        }}
      >
        {meta}
      </div>
    </div>
    {badge && (
      <span
        style={{
          marginLeft: "auto",
          fontSize: "11.5px",
          fontWeight: 600,
          color: badgeFg,
          background: badgeBg,
          borderRadius: 999,
          padding: "5px 11px",
          whiteSpace: "nowrap",
        }}
      >
        {badge}
      </span>
    )}
  </div>
);

const MockChips = ({ items }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {items.map((chip) => (
      <span
        key={chip}
        style={{
          minHeight: 30,
          padding: "0 13px",
          borderRadius: 999,
          border: `1px solid ${C.line}`,
          background: C.onDark,
          fontSize: "12.5px",
          color: C.body,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {chip}
      </span>
    ))}
  </div>
);

const MockPill = ({ tone = "solid", children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      height: 38,
      padding: "0 16px",
      borderRadius: 999,
      background: tone === "solid" ? C.orange : C.onDark,
      color: tone === "solid" ? C.onDark : C.ink,
      border: tone === "solid" ? "none" : `1px solid ${C.border}`,
      fontSize: "13.5px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

/* 01 — the company list, plus companies the recruiter adds */
const CompaniesIllustration = () => (
  <IllustrationCard id="companies">
    <Stack>
      <MockField
        icon={<IconBuildingPlus size={16} stroke={C.muted} />}
        action="Add"
        actionTone={C.orange}
      >
        Stone King
      </MockField>
      <MockRow
        title="stoneking.co.uk/careers"
        meta="Workday · 34 roles · checked 2h ago"
        monoMeta
        badge="Tracking"
      />
      <MockRow
        title="northwind.com/jobs"
        meta="Greenhouse · 18 roles · checked 4h ago"
        monoMeta
        badge="Tracking"
      />
      <MockRow
        title="orbitlabs.io/join-us"
        meta="Finding careers page…"
        monoMeta
        badge="Scanning"
        badgeFg={C.orange}
        badgeBg={C.orangeWash}
      />
    </Stack>
  </IllustrationCard>
);

/* 02 — scanned openings, or a job description the recruiter uploads */
const JobsIllustration = () => (
  <IllustrationCard id="jobs">
    <Stack>
      <div style={{ display: "flex", gap: 8 }}>
        {["Scanned jobs", "Upload a JD"].map((tab, i) => (
          <span
            key={tab}
            style={{
              height: 32,
              padding: "0 14px",
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              fontSize: "12.5px",
              fontWeight: 500,
              background: i === 0 ? C.deep : C.onDark,
              color: i === 0 ? C.onDark : C.body,
              border: i === 0 ? "none" : `1px solid ${C.line}`,
            }}
          >
            {tab}
          </span>
        ))}
      </div>
      <MockRow
        title="Restructuring Associate"
        meta="Stone King · London · 6 requirements captured"
        badge="New"
        badgeFg={C.orange}
        badgeBg={C.orangeWash}
      />
      <MockRow
        title="Senior Associate, Banking"
        meta="Stone King · London · 2 contacts attached"
        badge="Live"
      />
      <div
        style={{
          border: `1.5px dashed ${C.border}`,
          borderRadius: 12,
          padding: "16px 15px",
          textAlign: "center",
          fontSize: "13.5px",
          color: C.muted,
          background: C.onDark,
        }}
      >
        <b style={{ color: C.ink, fontWeight: 600 }}>Drop a job description</b>
        <span style={{ display: "block", marginTop: 4 }}>
          scoutm8 reads the requirements straight off the document
        </span>
      </div>
    </Stack>
  </IllustrationCard>
);

/* 03 — one click to match, one click to shortlist */
const CandidatesIllustration = () => (
  <IllustrationCard id="people-search">
    <Stack>
      <MockField
        icon={<IconUserSearch size={16} stroke={C.muted} />}
        action="Match"
      >
        Senior Restructuring Associate
      </MockField>
      <MockChips items={["Singapore", "5-8 yrs", "Magic Circle", "Mandarin"]} />
      <MockRow
        avatar={<Avatar initials="JT" bg={C.blueWash} fg={C.blue} />}
        title="Jonathan Tan"
        meta="Associate · Allen &amp; Gledhill"
        badge="95% fit"
      />
      <MockRow
        avatar={<Avatar initials="PS" bg={C.orangeWash} fg={C.orange} />}
        title="Priya Sharma"
        meta="Senior Associate · Rajah &amp; Tann"
        badge="89% fit"
      />
    </Stack>
  </IllustrationCard>
);

/* 04 — verified contact details, then an AI-written message */
const OutreachIllustration = () => (
  <IllustrationCard id="outreach">
    <Stack gap={16}>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <Avatar initials="EA" bg={C.blueWash} fg={C.blue} size={48} />
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>
            Erica Ang
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
            Head of HR · Stone King
          </div>
        </div>
        <span
          style={{
            marginLeft: "auto",
            fontSize: "11.5px",
            fontWeight: 600,
            color: C.blue,
            background: C.blueWash,
            borderRadius: 999,
            padding: "5px 11px",
          }}
        >
          Verified
        </span>
      </div>
      <Stack gap={9}>
        {[
          [<IconMail size={14} stroke={C.muted} key="m" />, "e.ang@stoneking.co.uk · direct"],
          [<IconPin size={14} stroke={C.muted} key="p" />, "London Area, United Kingdom"],
          [<IconConnections size={14} stroke={C.muted} key="c" />, "Head of HR · 6 yrs at firm"],
        ].map(([icon, label]) => (
          <div
            key={label}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              fontSize: "13.5px",
              color: C.body,
            }}
          >
            <span style={{ flexShrink: 0, display: "flex" }}>{icon}</span>
            {label}
          </div>
        ))}
      </Stack>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <MockPill>✦ Generate message</MockPill>
        <MockPill tone="ghost">Send</MockPill>
      </div>
    </Stack>
  </IllustrationCard>
);

/* 05 — the co-pilot answering with sourced market intelligence */
const CopilotIllustration = () => (
  <IllustrationCard id="copilot">
    <Stack>
      <MockField
        icon={<span style={{ color: C.orange }}>✦</span>}
        action="Ask"
      >
        Top news on Stone King?
        <span
          style={{
            width: "1.5px",
            height: 18,
            background: C.orange,
            animation: "sm8blink 1.1s step-end infinite",
          }}
        />
      </MockField>
      <MockChips
        items={["Hiring trends this quarter", "Who is the HR here?"]}
      />
      <div
        style={{
          border: `1px solid ${C.line}`,
          borderRadius: 12,
          background: C.wash,
          padding: "14px 16px",
          fontSize: "13.5px",
          lineHeight: 1.55,
          color: C.bodyStrong,
        }}
      >
        <b style={{ color: C.ink }}>Restructuring is expanding</b> — 3 senior
        roles posted this week, up from 1 last quarter.
        <span
          style={{
            display: "block",
            marginTop: 6,
            fontFamily: MONO,
            fontSize: "11.5px",
            color: C.muted,
          }}
        >
          Source: careers site · updated 2h ago · added to watchlist
        </span>
      </div>
    </Stack>
  </IllustrationCard>
);

/* Small mono step marker above each feature heading */
const FeatureStep = ({ children }) => (
  <div
    style={{
      fontFamily: MONO,
      fontSize: "12.5px",
      letterSpacing: ".1em",
      color: C.orange,
      marginBottom: 12,
    }}
  >
    {children}
  </div>
);

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.line}` }}>
      <FaqButton
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {question}
        <span
          style={{
            width: 22,
            height: 22,
            flexShrink: 0,
            color: C.orange,
            transition: "transform .25s ease",
            display: "grid",
            placeItems: "center",
            transform: `rotate(${open ? "45deg" : "0deg"})`,
          }}
        >
          <IconPlus size={22} />
        </span>
      </FaqButton>
      {open && (
        <div
          style={{
            padding: "0 0 24px",
            fontSize: 15,
            lineHeight: 1.65,
            color: C.body,
            maxWidth: 660,
          }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

/* The three workflow phases and their steps, from the scoutm8 deck */
const WORKFLOW = [
  {
    index: "01",
    phase: "Sourcing Jobs",
    summary:
      "Track every opening at your target firms through a single portal, in real time.",
    steps: [
      ["Track Relevant Companies", "Select any company of interest."],
      ["Identify Relevant Jobs", "Select the job openings worth working."],
    ],
  },
  {
    index: "02",
    phase: "Sourcing Candidates",
    summary: "Identify the best candidates for each role in minutes, not days.",
    steps: [
      ["Source Candidates", "Automatically receive candidate recommendations."],
      ["Reach Out to Candidates", "Generate and send tailored messages."],
    ],
  },
  {
    index: "03",
    phase: "Submitting Candidates & Admin",
    summary: "Manage every conversation and placement on one platform.",
    steps: [
      [
        "Connect with Hiring Managers",
        "Identify and connect with the decision-maker behind the role.",
      ],
      ["Track Responses", "Placements and replies are tracked automatically."],
    ],
  },
];

const COMPARE_MANUAL = [
  "Tabs open across a dozen careers pages",
  "Guessing who actually owns the hire",
  "Generic outreach that gets ignored",
  "Finding out about a role a week too late",
];
const COMPARE_SCOUTM8 = [
  "One desk, the entire job market",
  "The verified decision-maker, named",
  "Outreach written for that person's role",
  "Alerted the moment a role goes live",
];

/* ------------------------------------------------------------------ */
/* "Book a demo" — form that emails the enquiry to the sales inbox      */
/* ------------------------------------------------------------------ */

/* Above the app sidebar (1001) and its mobile panel (1100): the modal is also opened from
   Plans and Checkout, and the rail must blur under it like the rest of the page. */
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: color-mix(in srgb, ${C.deep} 42%, transparent);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  /* More air above and below the box on desktop-height screens. */
  @media (min-height: 700px) and (min-width: 561px) {
    padding: 56px 24px;
  }
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${C.onDark};
  border-radius: 22px;
  border: 1px solid ${C.line};
  box-shadow: 0 40px 90px -30px color-mix(in srgb, ${C.deep} 42%, transparent);
  padding: 36px 38px 32px;
  position: relative;
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  /* Desktop: the box reads best at 90% scale (zoom also shrinks max-height, so it fits too). */
  @media (min-width: 561px) {
    zoom: 0.9;
  }
  @media (min-height: 700px) and (min-width: 561px) {
    max-height: calc(100dvh - 112px);
  }

  @media (max-width: 560px) {
    padding: 26px 20px 22px;
    border-radius: 18px;
  }
`;

/* Logo, heading with one serif word, and a line of context: the top of every modal. */
function ModalHead({ title, accent, sub }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <img src={LOGO} alt="" aria-hidden="true" style={{ height: 22, width: "auto", display: "block", marginBottom: 18 }} />
      <h3
        style={{
          margin: 0,
          fontFamily: SANS,
          fontWeight: 600,
          fontSize: 28,
          lineHeight: 1.1,
          letterSpacing: "-0.025em",
          color: C.deep,
          textWrap: "balance",
        }}
      >
        {title} <Serif style={{ color: C.orange }}>{accent}</Serif>
      </h3>
      {sub && (
        <p style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.55, color: C.body, textWrap: "pretty" }}>{sub}</p>
      )}
    </div>
  );
}

// Sign-up header for a referral link: who invited them and the bonus waiting for them.
const RefGift = styled.div`
  position: relative;
  overflow: hidden;
  margin-top: 18px;
  padding: 18px 18px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${C.orangeWash} 0%, #fff8f2 100%);
  border: 1px solid #ffd9bd;

  &::after {
    content: "";
    position: absolute;
    right: -38px;
    top: -38px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 122, 31, 0.18), rgba(255, 122, 31, 0) 70%);
    pointer-events: none;
  }
  .from {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: ${C.bodyStrong};
  }
  .avatar {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${C.orange};
    color: ${C.onOrange};
    font-weight: 600;
    font-size: 13px;
  }
  .from b {
    color: ${C.ink};
    font-weight: 600;
  }
  .amount {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 12px;
    color: ${C.orangeInk};
  }
  .amount strong {
    font-family: ${SANS};
    font-size: 40px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.03em;
  }
  .amount span {
    font-size: 15px;
    font-weight: 500;
  }
  .terms {
    margin-top: 8px;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${C.body};
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 14px;
  }
  .chips span {
    padding: 4px 10px;
    border-radius: 99px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid #ffd9bd;
    font-size: 12px;
    font-weight: 500;
    color: ${C.bodyStrong};
  }
`;

function ReferralHead({ firstName, credits }) {
  const name = String(firstName || "").trim();
  return (
    <div style={{ marginBottom: 22 }}>
      <img src={LOGO} alt="" aria-hidden="true" style={{ height: 22, width: "auto", display: "block", marginBottom: 18 }} />
      <h3 style={{ margin: 0, fontFamily: SANS, fontWeight: 600, fontSize: 28, lineHeight: 1.1, letterSpacing: "-0.025em", color: C.deep, textWrap: "balance" }}>
        {name ? `${name} invited you to` : "You're invited to"} <Serif style={{ color: C.orange }}>scoutm8.</Serif>
      </h3>
      <RefGift>
        <div className="from">
          <span className="avatar" aria-hidden="true">{(name[0] || "★").toUpperCase()}</span>
          <span>
            A welcome gift from <b>{name || "your friend"}</b>
          </span>
        </div>
        <div className="amount">
          <strong>{Number(credits || 0).toLocaleString("en-US")}</strong>
          <span>bonus credits</span>
        </div>
        <div className="terms">Yours when you go on a paid plan before your free trial ends, on top of your plan's credits.</div>
        <div className="chips">
          <span>7 days free</span>
          <span>No card needed</span>
          <span>1,000 trial credits</span>
        </div>
      </RefGift>
    </div>
  );
}

// The plain sign-up's header: what the trial gives, set as type (big serif figure, small caps label).
const Perks = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 18px;
  padding: 14px 0 2px;
  border-top: 1px solid ${C.line};

  .perk {
    padding: 0 16px;
    min-width: 0;
  }
  .perk:first-child {
    padding-left: 0;
  }
  .perk + .perk {
    border-left: 1px solid ${C.line};
  }
  .fig {
    font-family: ${SERIF};
    font-style: italic;
    font-size: 38px;
    line-height: 1;
    letter-spacing: -0.02em;
    color: ${C.orange};
  }
  .fig em {
    font-style: italic;
  }
  .label {
    margin-top: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${C.muted};
    white-space: nowrap;
  }
`;

function SignupHead() {
  return (
    <div style={{ marginBottom: 22 }}>
      <img src={LOGO} alt="" aria-hidden="true" style={{ height: 22, width: "auto", display: "block", marginBottom: 18 }} />
      <h3 style={{ margin: 0, fontFamily: SANS, fontWeight: 600, fontSize: 28, lineHeight: 1.1, letterSpacing: "-0.025em", color: C.deep, textWrap: "balance" }}>
        Create your <Serif style={{ color: C.orange }}>account.</Serif>
      </h3>
      <p style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.55, color: C.body }}>Your own workspace to find, track and reach candidates.</p>
      <Perks>
        <div className="perk">
          <div className="fig">
            7 <em>days</em>
          </div>
          <div className="label">Free trial</div>
        </div>
        <div className="perk">
          <div className="fig">1,000</div>
          <div className="label">Credits to start</div>
        </div>
        <div className="perk">
          <div className="fig">
            <em>$</em>0
          </div>
          <div className="label">No card needed</div>
        </div>
      </Perks>
    </div>
  );
}

// Two fields on one row; the second slot is skipped for a seat invite (no company field).
const FieldPair = styled.div`
  display: grid;
  grid-template-columns: ${({ $single }) => ($single ? "1fr" : "1fr 1fr")};
  gap: 12px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

// Optional referral code on the plain sign-up, checked as it's typed.
const RefBox = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 12px;
  border: 1px dashed ${({ $ok }) => ($ok ? "#ffb98a" : C.border)};
  background: ${({ $ok }) => ($ok ? C.orangeWash : C.wash)};
  transition: background 0.2s ease, border-color 0.2s ease;

  .icon {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    background: ${({ $ok }) => ($ok ? C.orange : C.onDark)};
    color: ${({ $ok }) => ($ok ? C.onOrange : C.orange)};
    border: 1px solid ${({ $ok }) => ($ok ? C.orange : C.line)};
    transition: background 0.2s ease, color 0.2s ease;
  }
  .body {
    flex: 1;
    min-width: 0;
  }
  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: ${C.bodyStrong};
  }
  label small {
    font-size: 12.5px;
    font-weight: 400;
    color: ${C.muted};
  }
  input {
    margin-top: 8px;
    width: 100%;
    box-sizing: border-box;
    font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 15px;
    letter-spacing: 0.14em;
    color: ${C.ink};
    background: ${C.onDark};
    border: 1px solid ${C.line};
    border-radius: 10px;
    padding: 10px 12px;
    outline: none;
  }
  input:focus {
    border-color: ${C.orange};
    box-shadow: 0 0 0 3px ${C.orangeWash};
  }
  input::placeholder {
    letter-spacing: 0.14em;
    color: ${C.muted};
    opacity: 0.6;
  }
  .note {
    margin-top: 7px;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${C.muted};
  }
  .note.ok {
    color: ${C.orangeInk};
  }
  .note.ok b {
    font-weight: 600;
  }
`;

function RefCodeField({ value, onChange }) {
  const [found, setFound] = useState(null); // { code, firstName } | false (unknown) | null (not checked)
  const code = String(value || "").replace(/[^A-Z0-9]/g, "");
  useEffect(() => {
    setFound(null);
    if (code.length < 6) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      fetch(`/api/auth/referral?code=${encodeURIComponent(code)}`, { credentials: "include" })
        .then(async (res) => {
          if (!alive) return;
          if (res.ok) setFound(await res.json());
          else if (res.status === 404) setFound(false);
        })
        .catch(() => {});
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [code]);
  const ok = Boolean(found && found.code === code);
  return (
    <RefBox $ok={ok}>
      <div className="body">
        <label htmlFor="signup-ref">
          Referral code <small>(optional)</small>
        </label>
        <input
          id="signup-ref"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          autoComplete="off"
          spellCheck={false}
          placeholder="K7QM2XHP"
          maxLength={12}
        />
        {ok ? (
          <div className="note ok">
            <b>{found.firstName ? `${found.firstName}'s code applied.` : "Code applied."}</b> Get {Number(found.credits || 0).toLocaleString("en-US")} bonus credits when you go on a paid plan before your trial ends.
          </div>
        ) : found === false ? (
          <div className="note">We don't recognise that code. Check it, or leave it blank.</div>
        ) : (
          <div className="note">Got one from a friend? Add it now for bonus credits when you go on a paid plan. Codes can't be added after sign-up.</div>
        )}
      </div>
    </RefBox>
  );
}

/* Two short fields side by side; stacks on narrow screens. */
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  min-height: 34px;
  border-radius: 99px;
  border: 1px solid ${C.line};
  background: ${C.onDark};
  color: ${C.muted};
  display: grid;
  place-items: center;
  cursor: pointer;
  transition:
    color 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    color: ${C.ink};
    border-color: ${C.border};
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: ${C.bodyStrong};

  input,
  textarea {
    font-family: ${SANS};
    font-size: 15px;
    color: ${C.ink};
    background: ${C.wash};
    border: 1px solid ${C.line};
    border-radius: 12px;
    padding: 12px 14px;
    outline: none;
    transition:
      border-color 0.16s ease,
      background 0.16s ease,
      box-shadow 0.16s ease;

    /* iOS zooms the page into any field under 16px on focus */
    @media (max-width: 560px) {
      font-size: 16px;
    }
  }

  input::placeholder,
  textarea::placeholder {
    color: ${C.muted};
  }

  input:hover,
  textarea:hover {
    border-color: ${C.border};
  }

  input:focus,
  textarea:focus {
    background: ${C.onDark};
    border-color: ${C.blue};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${C.blue} 13%, transparent);
  }

  textarea {
    resize: vertical;
    min-height: 88px;
  }
`;

const FormError = styled.div`
  font-size: 13.5px;
  color: ${C.orangeInk};
  background: ${C.orangeWash};
  border: 1px solid color-mix(in srgb, ${C.orange} 28%, transparent);
  border-radius: 10px;
  padding: 10px 12px;
`;

// A referral link's code on the sign-up form: shown, not editable.
const LockedCode = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${C.line};
  background: ${C.wash};
  color: ${C.muted};

  b {
    font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: ${C.ink};
  }
  span {
    margin-left: auto;
    font-size: 12.5px;
    font-weight: 400;
  }
`;

const HoneyPot = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

function DemoModal({ open, onClose }) {
  const [values, setValues] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setStatus("idle");
    setError("");
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);
  useLockPageScroll(open);

  if (!open) return null;

  const set = (key) => (e) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setError("");
    setStatus("sending");
    try {
      const res = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Couldn't send that just now. Please try again.");
        setStatus("idle");
        return;
      }
      setStatus("sent");
    } catch (err) {
      setError("Couldn't reach us just now. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <ModalBackdrop>
      <ModalCard
        role="dialog"
        aria-modal="true"
        aria-label="Book a demo"
      >
        <ModalClose type="button" onClick={onClose} aria-label="Close">
          <IconCross size={16} width={2.2} />
        </ModalClose>

        {status === "sent" ? (
          <div style={{ textAlign: "center", padding: "18px 0 6px" }}>
            <span
              style={{
                width: 52,
                height: 52,
                borderRadius: 99,
                background: C.orangeWash,
                color: C.orange,
                display: "grid",
                placeItems: "center",
                margin: "0 auto 18px",
              }}
            >
              <IconCheck size={24} width={2.6} />
            </span>
            <h3
              style={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: 24,
                letterSpacing: "-.02em",
                margin: "0 0 10px",
                color: C.deep,
              }}
            >
              Thanks — that's with us.
            </h3>
            <p
              style={{
                margin: "0 auto 22px",
                fontSize: 15,
                lineHeight: 1.6,
                color: C.body,
                maxWidth: 340,
              }}
            >
              Someone from the team will be in touch shortly to set up your call.
            </p>
            <PrimaryButton type="button" $lg onClick={onClose}>
              Close
            </PrimaryButton>
          </div>
        ) : (
          <form onSubmit={submit}>
            <ModalHead title="Book a" accent="demo." sub="Tell us a little about your desk and we'll set up a call." />

            <div
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <Field>
                Name
                <input
                  ref={firstFieldRef}
                  value={values.name}
                  onChange={set("name")}
                  autoComplete="name"
                  required
                />
              </Field>
              <Field>
                Email
                <input
                  type="email"
                  value={values.email}
                  onChange={set("email")}
                  autoComplete="email"
                  required
                />
              </Field>
              <FieldRow>
                <Field>
                  Company
                  <input
                    value={values.company}
                    onChange={set("company")}
                    autoComplete="organization"
                  />
                </Field>
                <Field>
                  <span>
                    Phone <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span>
                  </span>
                  <input
                    value={values.phone}
                    onChange={set("phone")}
                    autoComplete="tel"
                  />
                </Field>
              </FieldRow>
              <Field>
                What are you hoping to solve?
                <textarea value={values.message} onChange={set("message")} />
              </Field>

              <HoneyPot aria-hidden="true">
                <label>
                  Website
                  <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={set("website")}
                  />
                </label>
              </HoneyPot>

              {error && <FormError>{error}</FormError>}

              <PrimaryButton
                type="submit"
                $lg
                disabled={status === "sending"}
                style={{ width: "100%", marginTop: 4 }}
              >
                {status === "sending" ? "Sending…" : "Send"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </ModalCard>
    </ModalBackdrop>
  );
}

/* Create an account in place: the password is chosen here, the login that follows is
   Auth0's with the email prefilled, and `next` is where the browser lands after it. */
export function SignupModal({ open, onClose, invite = null, referral = null }) {
  const { login } = useAuth();
  const [values, setValues] = useState({ firstName: "", lastName: "", company: "", email: "", website: "", referral: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");
  const [exists, setExists] = useState(false);
  const [resent, setResent] = useState("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setStatus("idle");
    setError("");
    setExists(false);
    setResent("");
    // A seat invite fixes the email and the company; only name and password are typed.
    if (invite) setValues((v) => ({ ...v, email: invite.email || "", company: invite.companyName || "" }));
    if (referral?.code) setValues((v) => ({ ...v, referral: referral.code }));
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open, invite, referral]);
  useLockPageScroll(open);

  if (!open) return null;

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const email = (invite?.email || values.email).trim().toLowerCase();
  const toLogin = () => login({ loginHint: email });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setError("");
    setExists(false);
    if (!values.firstName.trim()) {
      setError("Please add your first name.");
      return;
    }
    if (!values.company.trim()) {
      setError("Please add your company name.");
      return;
    }
    setStatus("sending");
    // The form carries the code itself, so the link's saved copy is no longer needed.
    storeReferral(null);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          company: invite ? invite.companyName : values.company,
          email,
          website: values.website,
          invite: invite?.token || undefined,
          // A referral link's code is fixed; otherwise whatever was typed.
          referral: invite ? undefined : referral?.code || values.referral.trim() || undefined,
          referralVia: referral?.code ? "link" : "code",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok && body?.code === "PENDING") {
        // A link is already on its way to this address: show the same waiting screen, with Resend.
        setStatus("sent");
        return;
      }
      if (!res.ok) {
        setExists(body?.code === "EXISTS");
        throw new Error(body?.error || "We couldn't create your account just now. Please try again.");
      }
      setStatus("sent");
    } catch (err) {
      setError(err.message || "We couldn't create your account just now. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <ModalBackdrop>
      <ModalCard role="dialog" aria-modal="true" aria-label="Create your account" style={{ maxWidth: invite ? 460 : 600 }}>
        <ModalClose type="button" onClick={onClose} aria-label="Close">
          <IconCross size={16} />
        </ModalClose>
        {status === "sent" ? (
          <div style={{ textAlign: "center", padding: "18px 0 6px" }}>
            <span
              style={{
                width: 52,
                height: 52,
                borderRadius: 99,
                background: C.orangeWash,
                color: C.orange,
                display: "grid",
                placeItems: "center",
                margin: "0 auto 18px",
              }}
            >
              <IconMail size={24} width={2.2} />
            </span>
            <h3 style={{ fontFamily: SANS, fontWeight: 600, fontSize: 24, letterSpacing: "-.02em", margin: "0 0 10px", color: C.deep }}>
              Check your inbox.
            </h3>
            <p style={{ margin: "0 auto 22px", fontSize: 15, lineHeight: 1.6, color: C.body, maxWidth: 340 }}>
              We sent a confirmation link to <strong style={{ color: C.ink, fontWeight: 500 }}>{email}</strong>. Click
              it, choose a password, and {invite ? `you're in the ${invite.companyName} workspace.` : "your workspace opens with your free trial."}
            </p>
            <ResendLink email={email} onDone={setResent} />
            {resent && <p style={{ margin: "12px 0 0", fontSize: 13, color: C.muted }}>{resent}</p>}
          </div>
        ) : (
        <form onSubmit={submit} noValidate>
          {invite ? (
            <ModalHead
              title="Join"
              accent={`${invite.companyName}.`}
              sub={`${invite.inviterName ? `${invite.inviterName} invited you.` : "You've been invited."} Create your login and you're in, with your own credits.`}
            />
          ) : referral?.code ? (
            <ReferralHead firstName={referral.firstName} credits={referral.credits} />
          ) : (
            <SignupHead />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field>
                First name
                <input ref={firstFieldRef} value={values.firstName} onChange={set("firstName")} autoComplete="given-name" required />
              </Field>
              <Field>
                Last name
                <input value={values.lastName} onChange={set("lastName")} autoComplete="family-name" />
              </Field>
            </div>
            <FieldPair $single={Boolean(invite)}>
            {!invite && (
              <Field>
                Company
                <input value={values.company} onChange={set("company")} autoComplete="organization" required />
              </Field>
            )}
            <Field>
              Email
              <input
                type="email"
                value={invite ? invite.email : values.email}
                onChange={set("email")}
                autoComplete="email"
                placeholder="you@yourfirm.com"
                readOnly={Boolean(invite)}
                style={invite ? { color: C.muted, background: C.wash || "#f6f8fb" } : undefined}
                required
              />
            </Field>
            </FieldPair>
            {!invite && referral?.code ? (
              <Field as="div">
                Referral code
                <LockedCode>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  <b>{referral.code}</b>
                  <span>{referral.firstName ? `From ${referral.firstName}'s link` : "From your invite link"}</span>
                </LockedCode>
              </Field>
            ) : (
              !invite && <RefCodeField value={values.referral} onChange={(code) => setValues((v) => ({ ...v, referral: code }))} />
            )}
            <HoneyPot aria-hidden="true">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
              </label>
            </HoneyPot>
            {error && (
              <FormError>
                {error}{" "}
                {exists && (
                  <button
                    type="button"
                    onClick={toLogin}
                    style={{ border: 0, background: "none", font: "inherit", color: C.blue, cursor: "pointer", padding: 0, minHeight: 0 }}
                  >
                    Log in
                  </button>
                )}
              </FormError>
            )}
            <PrimaryButton type="submit" $lg disabled={status === "sending"} style={{ width: "100%", marginTop: 4 }}>
              {status === "sending" ? "Sending your link…" : "Create account"}
            </PrimaryButton>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: C.muted, textAlign: "center" }}>
              By continuing you agree to the{" "}
              <a href="/terms" style={{ color: C.blue, textDecoration: "none" }}>Terms</a> and{" "}
              <a href="/privacy-policy" style={{ color: C.blue, textDecoration: "none" }}>Privacy Policy</a>.
              {" "}Already have an account?{" "}
              <button
                type="button"
                onClick={toLogin}
                style={{ border: 0, background: "none", font: "inherit", color: C.blue, cursor: "pointer", padding: 0, minHeight: 0 }}
              >
                Log in
              </button>
            </p>
          </div>
        </form>
        )}
      </ModalCard>
    </ModalBackdrop>
  );
}

/* The step behind the confirmation link (?setpw=<token>): choose a password, which creates the
   login and signs the person in. The token is the proof the address was confirmed. */
function SetPasswordModal() {
  const [token] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("setpw") || "";
      if (t) window.history.replaceState(null, "", window.location.pathname);
      return t;
    } catch {
      return "";
    }
  });
  const email = useMemo(() => {
    try {
      return JSON.parse(atob(token.split(".")[0].replace(/-/g, "+").replace(/_/g, "/"))).e || "";
    } catch {
      return "";
    }
  }, [token]);
  const [open, setOpen] = useState(Boolean(token));
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [gone, setGone] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const strongEnough = (p) => p.length >= 8 && [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(p)).length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (!strongEnough(password)) {
      setError("Choose a stronger password: at least 8 characters, mixing letters, numbers and symbols.");
      return;
    }
    if (password !== again) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/complete-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGone(body?.code === "EXPIRED" || body?.code === "INVITE_GONE" || body?.code === "EXISTS");
        throw new Error(body?.error || "We couldn't create your account just now. Please try again.");
      }
      window.location.assign(body.next || `/api/auth/login?login_hint=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(err.message || "We couldn't create your account just now. Please try again.");
      setBusy(false);
    }
  };

  const passwordInput = (value, onChange, inputRef, placeholder) => (
    <div style={{ position: "relative", display: "flex" }}>
      <input
        ref={inputRef}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        placeholder={placeholder}
        minLength={8}
        maxLength={128}
        required
        style={{ flex: 1, paddingRight: 64 }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-pressed={show}
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          border: 0,
          background: "none",
          font: "inherit",
          fontSize: 12.5,
          color: C.blue,
          cursor: "pointer",
          padding: "4px 6px",
        }}
      >
        {show ? "Hide" : "Show"}
      </button>
    </div>
  );

  return (
    <ModalBackdrop>
      <ModalCard role="dialog" aria-modal="true" aria-label="Choose your password" style={{ maxWidth: 460 }}>
        <ModalClose type="button" onClick={() => setOpen(false)} aria-label="Close">
          <IconCross size={16} />
        </ModalClose>
        <form onSubmit={submit} noValidate style={{ display: "grid", gap: 14 }}>
          <ModalHead
            title="Email confirmed."
            accent="Choose a password."
            sub={email ? `For ${email}. At least 8 characters, mixing letters, numbers and symbols.` : "At least 8 characters, mixing letters, numbers and symbols."}
          />
          <Field>
            Password
            {passwordInput(password, setPassword, firstFieldRef, "8+ characters")}
          </Field>
          <Field>
            Confirm password
            {passwordInput(again, setAgain, null, "Type it again")}
          </Field>
          {error && (
            <FormError>
              {error}
              {gone && " "}
              {gone && (
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{ border: 0, background: "none", font: "inherit", color: C.blue, cursor: "pointer", padding: 0, minHeight: 0 }}
                >
                  Back to the site
                </button>
              )}
            </FormError>
          )}
          <PrimaryButton type="submit" $lg disabled={busy} style={{ width: "100%", marginTop: 4 }}>
            {busy ? "Creating your account…" : "Create account and log in"}
          </PrimaryButton>
        </form>
      </ModalCard>
    </ModalBackdrop>
  );
}

/* "Resend" for the confirmation email; the same wording whether or not the address is known. */
function ResendLink({ email, onDone }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      onDone(res.ok ? "Sent again. Give it a minute, and check spam." : "We couldn't send it just now. Try again shortly.");
    } catch {
      onDone("We couldn't send it just now. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <GhostLink as="button" type="button" onClick={resend} disabled={busy} style={{ height: 42, padding: "0 20px", fontSize: 14 }}>
      {busy ? "Sending…" : "Resend email"}
    </GhostLink>
  );
}

/* Slim notice under the nav after a login was refused for an unconfirmed address. */
// "Log in" stops here first so visitors without an account find sign-up instead of a dead end on Auth0.
export function LoginModal({ open, onClose, onSignup, reauth = false, invite = null }) {
  const { login } = useAuth();
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) setPending(false);
  }, [open]);
  // Back from the Auth0 page restores this page from the bfcache with the redirect still pending; re-enable the button.
  useEffect(() => {
    const reset = () => setPending(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  useLockPageScroll(open);

  if (!open) return null;

  const go = () => {
    if (pending) return;
    setPending(true);
    login({ reauth, loginHint: invite?.email || "" }); // full-page Auth0 redirect
  };

  return (
    <ModalBackdrop>
      <ModalCard role="dialog" aria-modal="true" aria-label="Log in" style={{ maxWidth: 400 }}>
        <ModalClose type="button" onClick={onClose} aria-label="Close">
          <IconCross size={16} />
        </ModalClose>
        {invite ? (
          <ModalHead
            title="Join"
            accent={`${invite.companyName}.`}
            sub={`${invite.inviterName ? `${invite.inviterName} invited you.` : "You've been invited."} You already have a scoutm8 login for ${invite.email}, so just log in and you're in.`}
          />
        ) : (
          <ModalHead title="Welcome" accent="back." sub="Log in with the email you signed up with." />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <PrimaryButton type="button" $lg onClick={go} disabled={pending} autoFocus style={{ width: "100%" }}>
            {pending ? "Opening log in…" : invite ? "Log in and join" : "Log in"}
          </PrimaryButton>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: C.muted, textAlign: "center" }}>
            Don't have an account?{" "}
            <button
              type="button"
              onClick={onSignup}
              style={{ border: 0, background: "none", font: "inherit", color: C.blue, cursor: "pointer", padding: 0, minHeight: 0 }}
            >
              Sign up instead
            </button>
          </p>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

// A join link that no longer works: say so where the person landed, with a way to dismiss it.
function InviteNote({ text, onClose }) {
  return (
    <div
      role="status"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 1300,
        maxWidth: 520,
        width: "calc(100% - 32px)",
        background: C.deep,
        color: "#fff",
        borderRadius: 14,
        padding: "14px 18px",
        display: "flex",
        gap: 14,
        alignItems: "center",
        fontSize: 14,
        lineHeight: 1.5,
        boxShadow: "0 18px 40px rgba(6,32,61,.25)",
      }}
    >
      <span style={{ flex: 1 }}>{text}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" style={{ border: 0, background: "none", color: "#fff", cursor: "pointer", padding: 4 }}>
        <IconCross size={14} />
      </button>
    </div>
  );
}

function VerifyNotice() {
  const [email, setEmail] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("verify") || "";
      if (v) window.history.replaceState(null, "", window.location.pathname);
      return v;
    } catch {
      return "";
    }
  });
  const [note, setNote] = useState("");
  if (!email) return null;
  const expired = email === "expired" || email === "error";
  const weak = email === "weak";
  const inviteGone = email === "invite_gone";
  const special = expired || weak || inviteGone;
  return (
    <div style={{ background: C.orangeWash, borderBottom: `1px solid color-mix(in srgb, ${C.orange} 25%, transparent)` }}>
      <Container
        style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 18px", paddingTop: 12, paddingBottom: 12, fontSize: 14, color: C.orangeInk }}
      >
        <span style={{ flex: "1 1 320px" }}>
          {expired && "That confirmation link has expired. Sign up again to get a new one."}
          {weak && "That password didn't meet the requirements. Sign up again with at least 8 characters, mixing letters, numbers and symbols."}
          {inviteGone && "That invite is no longer valid. Ask your account admin to invite you again."}
          {!special && <>Confirm your email first. We sent a link to <strong style={{ fontWeight: 500 }}>{email}</strong>.</>}
          {note && <span style={{ marginLeft: 10, color: C.body }}>{note}</span>}
        </span>
        {!special && <ResendLink email={email} onDone={setNote} />}
        <button
          type="button"
          onClick={() => setEmail("")}
          aria-label="Dismiss"
          style={{ border: 0, background: "none", color: C.orangeInk, cursor: "pointer", font: "inherit", padding: 4 }}
        >
          <IconCross size={16} />
        </button>
      </Container>
    </div>
  );
}

export default function LandingPage() {
  const { sessionInvalidated } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  // /signup (the link in outreach emails) lands with the sign-up form already open.
  const [signupOpen, setSignupOpen] = useState(() => window.location.pathname === "/signup");
  // /login (also where the backend bounces a failed sign-in) lands with the sign-in form already open.
  const [loginOpen, setLoginOpen] = useState(() => window.location.pathname === "/login");
  // Closing either form on its own URL leaves the visitor on the plain landing page URL.
  const leaveEntryPath = (path) => {
    if (window.location.pathname === path) window.history.replaceState(null, "", "/" + window.location.search);
  };
  const closeSignup = () => {
    setSignupOpen(false);
    leaveEntryPath("/signup");
  };
  const closeLogin = () => {
    setLoginOpen(false);
    leaveEntryPath("/login");
  };
  // A seat invite link (?join=<token>): fetch who invited them where, then open the join step.
  const [invite, setInvite] = useState(null);
  const [inviteNote, setInviteNote] = useState("");
  // A referral link (/signup?ref=<code>): check the code, remember it, then open sign-up with it filled in.
  const [referral, setReferral] = useState(null);
  useEffect(() => {
    let code = "";
    try {
      if (window.location.pathname === "/signup") code = (new URLSearchParams(window.location.search).get("ref") || "").trim();
    } catch {
      code = "";
    }
    if (!code) return undefined;
    let alive = true;
    fetch(`/api/auth/referral?code=${encodeURIComponent(code)}`, { credentials: "include" })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive || !res.ok) return;
        const ref = { code: body.code, firstName: body.firstName || "", credits: body.credits || 0 };
        setReferral(ref);
        storeReferral({ code: ref.code, via: "link" });
        setSignupOpen(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    let token = "";
    try {
      token = new URLSearchParams(window.location.search).get("join") || "";
    } catch {
      token = "";
    }
    if (!token) return undefined;
    let alive = true;
    fetch(`/api/auth/invite?token=${encodeURIComponent(token)}`, { credentials: "include" })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setInviteNote(body?.error || "This invite link is no longer valid.");
          return;
        }
        const info = { ...body, token };
        setInvite(info);
        if (info.hasLogin) setLoginOpen(true);
        else setSignupOpen(true);
      })
      .catch(() => alive && setInviteNote("We couldn't load this invite just now. Please try the link again."));
    return () => {
      alive = false;
    };
  }, []);
  // Set when the BFF callback bounced us here with ?error=…: the user just authenticated,
  // so the retry must NOT force a federated logout (which would demand the password again).
  const [callbackError] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const err = q.get("error") || "";
      if (err) {
        console.warn("Sign-in callback error:", err, q.get("reason") || "");
        window.history.replaceState(null, "", window.location.pathname);
      }
      return err;
    } catch (_) {
      return "";
    }
  });

  // Every signed-out visitor gets a 401 on the session check, so the modal must open regardless; the redirect carries reauth.
  const handleLogin = () => setLoginOpen(true);


  const faqs = [
    {
      question: "Where do the roles come from?",
      answer:
        "scoutm8 continuously aggregates openings directly from the careers sites and job boards of the companies you choose to track, across every industry, refreshed throughout the day.",
    },
    {
      question: "What if a company I cover isn't on the platform?",
      answer:
        "Add it yourself. Type the firm's name and scoutm8 locates its careers page, works out which system sits behind it, and starts capturing openings — usually within minutes. It then re-checks on a schedule, so new roles reach you without anyone asking for them.",
    },
    {
      question: "Can I bring my own candidates?",
      answer:
        "Yes. Upload CVs, import a spreadsheet, or add candidates one by one, and they're parsed into the same structured profile as everyone else — experience, skills, seniority, location — so they're matched against live roles alongside the rest of your pool.",
    },
    {
      question: "How does the candidate search work?",
      answer:
        "Describe the role, or paste the brief, and scoutm8 finds the people for you — filtering on title, seniority, location, current firm, school, or language, then ranking what comes back against the role's requirements. Every search is saved, so you can reopen a shortlist any time.",
    },
    {
      question: "How accurate is the candidate matching?",
      answer:
        "Rather than matching keywords, the AI reads a CV's experience, skills, and seniority, then weighs each against the role's real requirements — returning a ranked fit score so you start with the strongest leads.",
    },
    {
      question: "Are the HR and hiring-manager contacts verified?",
      answer: `Yes. Every decision-maker in scoutm8 is verified before it reaches you, complete with role, location, and career context so your outreach is targeted, not scattershot.`,
    },
    {
      question: "Is my candidate data kept private?",
      answer:
        "Your candidates are yours. Records you add are encrypted in the database rather than stored in the clear, held in a store dedicated to your company rather than pooled with other clients', and reachable only by your team — with access recorded in an audit log.",
    },
    {
      question: "Can I track specific companies or roles?",
      answer:
        "Build a watchlist of the companies, roles, and countries you care about, and scoutm8 alerts you the moment a matching role, contact, or market shift appears — so you're first, not late.",
    },
  ];

  return (
    <Page>
      <LandingGlobalStyle />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <SignupModal open={signupOpen} onClose={closeSignup} invite={invite} referral={referral} />
      <LoginModal
        open={loginOpen}
        reauth={sessionInvalidated && !callbackError}
        invite={invite}
        onClose={closeLogin}
        onSignup={() => {
          closeLogin();
          setSignupOpen(true);
        }}
      />
      {inviteNote && <InviteNote text={inviteNote} onClose={() => setInviteNote("")} />}

      <Nav>
        <NavInner>
          <NavLogo href="#top">
            <img src={LOGO} alt="scoutm8" />
          </NavLogo>
          <NavLinks>
            {LANDING_LINKS.map((l) => (
              <NavLink key={l.href} href={l.href}>
                {l.label}
              </NavLink>
            ))}
          </NavLinks>
          <MarketingNavActions
            links={LANDING_LINKS}
            onDemo={() => setDemoOpen(true)}
            onSignup={() => setSignupOpen(true)}
            onLogin={handleLogin}
          />
        </NavInner>
      </Nav>

      <VerifyNotice />
      <SetPasswordModal />

      <HeroWrap id="top">
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: -140,
            width: 960,
            height: 560,
            transform: "translateX(-50%)",
            background:
              `radial-gradient(ellipse at center, color-mix(in srgb, ${C.blue} 13%, transparent), transparent 66%)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: 60,
            width: 520,
            height: 320,
            transform: "translateX(-50%)",
            background:
              `radial-gradient(ellipse at center, color-mix(in srgb, ${C.orange} 10%, transparent), transparent 68%)`,
            pointerEvents: "none",
          }}
        />

        <Container style={{ position: "relative" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              background: C.wash,
              border: `1px solid ${C.line}`,
              borderRadius: 999,
              padding: "6px 15px 6px 7px",
              fontSize: 13,
              color: C.body,
              marginBottom: 30,
            }}
          >
            <span
              style={{
                background: C.orange,
                color: C.onDark,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: ".03em",
                padding: "3px 10px",
                borderRadius: 999,
              }}
            >
              AI
            </span>
            Intelligence for modern recruiters
          </div>
          <H1>
            Recruitment,{" "}
            <Serif style={{ color: C.blue }}>Made Simple.</Serif>
          </H1>
          <p
            style={{
              margin: "26px auto 0",
              fontSize: 19,
              lineHeight: 1.55,
              color: C.body,
              maxWidth: 600,
              textWrap: "pretty",
            }}
          >
            Spend more time in conversations and less time at your desk.
          </p>
          <HeroCTAs>
            <GhostLink href="#features">See how it works</GhostLink>
          </HeroCTAs>
        </Container>

        <HeroDemo />
      </HeroWrap>

      <Section id="how" style={{ background: C.wash }}>
        <Container>
          <Reveal>
            <SectionHead $tight style={{ marginBottom: 64 }}>
              <Eyebrow>How it works</Eyebrow>
              <H2>
                Sourcing to placement, in{" "}
                <Serif style={{ color: C.orange }}>a few clicks.</Serif>
              </H2>
              <p
                style={{
                  margin: "16px 0 0",
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: C.body,
                  textWrap: "pretty",
                }}
              >
                One dashboard turns multi-day sourcing and outreach into a few
                minutes — a few clicks for new roles, a drag and drop for the
                ones you already run.
              </p>
            </SectionHead>
          </Reveal>
          <ThreeCol>
            {WORKFLOW.map(({ index, phase, summary, steps }) => (
              <Reveal
                key={index}
                style={{ paddingTop: 26, borderTop: `2px solid ${C.deep}` }}
              >
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: "12.5px",
                    letterSpacing: ".1em",
                    color: C.orange,
                    marginBottom: 16,
                  }}
                >
                  {index}
                </div>
                <h3
                  style={{
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: 22,
                    letterSpacing: "-.015em",
                    margin: "0 0 10px",
                    color: C.deep,
                  }}
                >
                  {phase}
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14.5px",
                    lineHeight: 1.6,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  {summary}
                </p>
                <div
                  style={{
                    margin: "22px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                >
                  {steps.map(([title, detail]) => (
                    <div
                      key={title}
                      style={{
                        display: "flex",
                        gap: 11,
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 99,
                          flexShrink: 0,
                          marginTop: 2,
                          background: C.orangeWash,
                          color: C.orange,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <IconCheck size={11} width={3} />
                      </span>
                      <div>
                        <div
                          style={{
                            fontSize: "14.5px",
                            fontWeight: 600,
                            color: C.ink,
                          }}
                        >
                          {title}
                        </div>
                        <div
                          style={{
                            fontSize: "13.5px",
                            lineHeight: 1.5,
                            color: C.body,
                            marginTop: 2,
                          }}
                        >
                          {detail}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </ThreeCol>
          <Reveal
            style={{
              marginTop: 56,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: C.onDark,
                border: `1px solid ${C.line}`,
                borderRadius: 999,
                padding: "10px 20px",
                fontSize: "14.5px",
                color: C.bodyStrong,
                textAlign: "center",
              }}
            >
              <i
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 99,
                  background: C.orange,
                  display: "block",
                  flexShrink: 0,
                }}
              />
              {/* One flex child, or the container gap lands mid-sentence */}
              <span>
                The whole process takes as little as{" "}
                <b style={{ color: C.deep, fontWeight: 600 }}>five minutes</b>
              </span>
            </span>
          </Reveal>
        </Container>
      </Section>

      <Section id="features">
        <Container>
          <Reveal>
            <SectionHead>
              <Eyebrow>Inside the platform</Eyebrow>
              <H2>
                Everything you need to place top talent{" "}
                <Serif style={{ color: C.orange }}>faster.</Serif>
              </H2>
              <p
                style={{
                  margin: "16px 0 0",
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: C.body,
                  textWrap: "pretty",
                }}
              >
                In the order you actually work: choose the companies, pull their
                jobs, match the candidates, then reach out.
              </p>
            </SectionHead>
          </Reveal>

          <Reveal>
            <TwoCol>
              <div data-copy>
                <FeatureIcon tone="blue">
                  <IconBuildingPlus />
                </FeatureIcon>
                <FeatureStep>01 · Companies</FeatureStep>
                <H3>Start with the firms you cover.</H3>
                <p
                  style={{
                    margin: "16px 0 0",
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  Pick from scoutm8's existing list of tracked companies, or add
                  any firm you want covered. scoutm8 finds its careers page,
                  works out the system behind it, and starts capturing the
                  openings — no integration, no waiting list.
                </p>
                <div
                  style={{
                    margin: "24px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Bullet>
                    Live job postings, sourced straight from their careers page
                  </Bullet>
                  <Bullet>
                    Key hiring managers and internal recruiters, verified
                  </Bullet>
                </div>
              </div>
              <CompaniesIllustration />
            </TwoCol>
          </Reveal>

          <Reveal>
            <TwoCol $spaced>
              <JobsIllustration />
              <div data-copy>
                <FeatureIcon tone="orange">
                  <IconDoc />
                </FeatureIcon>
                <FeatureStep>02 · Jobs</FeatureStep>
                <H3>Every opening, scanned and current.</H3>
                <p
                  style={{
                    margin: "16px 0 0",
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  Work the roles scoutm8 has already scanned from a company's
                  site, or upload a job description of your own. Either way the
                  hiring requirements and the contacts behind the role come
                  attached.
                </p>
                <div
                  style={{
                    margin: "24px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Bullet>
                    Unfilled openings captured with their requirements
                  </Bullet>
                  <Bullet>
                    Maintained continuously, so dated roles don't linger
                  </Bullet>
                </div>
              </div>
            </TwoCol>
          </Reveal>

          <Reveal>
            <TwoCol $spaced>
              <div data-copy>
                <FeatureIcon tone="blue">
                  <IconUserSearch />
                </FeatureIcon>
                <FeatureStep>03 · Candidates</FeatureStep>
                <H3>Matching in two clicks, not two days.</H3>
                <p
                  style={{
                    margin: "16px 0 0",
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  Sourcing eats close to 40% of a recruiter's week. One click
                  matches candidates against the role's requirements with
                  scoutm8's AI; a second saves the best of them for outreach.
                </p>
                <div
                  style={{
                    margin: "24px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Bullet>
                    Ranked on the role's real requirements, not keyword overlap
                  </Bullet>
                  <Bullet>
                    Shortlists save straight into your outreach list
                  </Bullet>
                </div>
              </div>
              <CandidatesIllustration />
            </TwoCol>
          </Reveal>

          <Reveal>
            <TwoCol $spaced>
              <OutreachIllustration />
              <div data-copy>
                <FeatureIcon tone="orange">
                  <IconMail />
                </FeatureIcon>
                <FeatureStep>04 · Outreach</FeatureStep>
                <H3>Contacts, message, sent.</H3>
                <p
                  style={{
                    margin: "16px 0 0",
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  Source contact details for candidates and hiring managers from
                  scoutm8's own database, let the AI tailor the message to
                  whoever is receiving it, then send — all without leaving the
                  platform.
                </p>
                <div
                  style={{
                    margin: "24px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Bullet>
                    Verified decision-makers, with role and location
                  </Bullet>
                  <Bullet>
                    Messages written for the person on the other end
                  </Bullet>
                </div>
              </div>
            </TwoCol>
          </Reveal>

          <Reveal>
            <TwoCol $spaced>
              <div data-copy>
                <FeatureIcon tone="blue">
                  <IconBot />
                </FeatureIcon>
                <FeatureStep>05 · Insights</FeatureStep>
                <H3>Market intelligence, on demand.</H3>
                <p
                  style={{
                    margin: "16px 0 0",
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: C.body,
                    textWrap: "pretty",
                  }}
                >
                  An AI co-pilot that asks the questions you would — tracking how
                  a firm's hiring moves over time and surfacing the signals worth
                  acting on before your competition sees them.
                </p>
                <div
                  style={{
                    margin: "24px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Bullet>
                    Hiring trends tracked over time and across jurisdictions
                  </Bullet>
                  <Bullet>
                    Recent signals — news, key projects, moves — with a source
                  </Bullet>
                </div>
              </div>
              <CopilotIllustration />
            </TwoCol>
          </Reveal>
        </Container>
      </Section>

      <Section id="why" style={{ background: C.wash }}>
        <Container>
          <Reveal>
            <SectionHead $tight>
              <Eyebrow>Why scoutm8</Eyebrow>
              <H2>
                The same hour, spent{" "}
                <Serif style={{ color: C.orange }}>completely differently.</Serif>
              </H2>
            </SectionHead>
          </Reveal>
          <CompareGrid>
            <Reveal
              style={{
                borderRadius: 20,
                padding: "34px 32px",
                border: `1px solid ${C.line}`,
                background: C.wash,
              }}
            >
              <div
                style={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 20,
                  color: C.muted,
                }}
              >
                Manual sourcing
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 15 }}
              >
                {COMPARE_MANUAL.map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      fontSize: 15,
                      lineHeight: 1.45,
                      color: C.body,
                    }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 99,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        marginTop: 1,
                        background: C.rule,
                        color: C.muted,
                      }}
                    >
                      <IconCross size={12} width={2.6} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal
              style={{
                borderRadius: 20,
                padding: "34px 32px",
                border: `1px solid ${C.deep}`,
                background: C.deep,
              }}
            >
              <div
                style={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 20,
                  color: C.amber,
                }}
              >
                With scoutm8
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 15 }}
              >
                {COMPARE_SCOUTM8.map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      fontSize: 15,
                      lineHeight: 1.45,
                      color: `color-mix(in srgb, ${C.onDark} 92%, transparent)`,
                    }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 99,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        marginTop: 1,
                        background: `color-mix(in srgb, ${C.orange} 22%, transparent)`,
                        color: C.amber,
                      }}
                    >
                      <IconCheck size={12} width={2.8} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </Reveal>
          </CompareGrid>
        </Container>
      </Section>

      <section
        id="stats"
        style={{
          padding: "96px 0",
          borderTop: `1px solid ${C.hair}`,
          borderBottom: `1px solid ${C.hair}`,
        }}
      >
        <Container>
          <StatGrid>
            {/* Numbers that hold by design, not by what the database happens to contain today. */}
            {[
              { value: 15, suffix: " min", label: "From brief to a ranked shortlist" },
              { value: 30, suffix: " s", label: "To draft a personalised outreach email" },
              { value: 24, suffix: "h", label: "Career-site refresh cycle" },
              { value: 50, suffix: "%", serif: true, label: "Of a recruiter's day saved" },
            ].map(({ value, suffix = "", serif = false, label }) => (
              <Reveal key={label}>
                <CountUp value={value} suffix={suffix} serif={serif} />
                <div
                  style={{ marginTop: 12, fontSize: "13.5px", color: C.muted }}
                >
                  {label}
                </div>
              </Reveal>
            ))}
          </StatGrid>
        </Container>
      </section>

      <Section style={{ background: C.wash }}>
        <Container>
          <Reveal style={{ textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: 88,
                lineHeight: 0.5,
                color: C.orange,
                height: 44,
              }}
            >
              “
            </div>
            <Quote>
              scoutm8 will turn a morning of tab-juggling into{" "}
              <em style={{ fontStyle: "italic", color: C.blue }}>ten minutes</em>
              — the role, the person behind it, and a draft email ready to send,
              before a competitor even knows the job is open.
            </Quote>
            <div
              style={{
                marginTop: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 13,
              }}
            >
              <span
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 99,
                  background: C.blueWash,
                  color: C.blue,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <IconUser size={21} />
              </span>
              <div style={{ textAlign: "left" }}>
                <div
                  style={{ fontSize: "14.5px", fontWeight: 600, color: C.ink }}
                >
                  CEO
                </div>
                <div style={{ fontSize: 13, color: C.muted }}>
                  Leading recruitment firm
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Section id="faq">
        <Container>
          <Reveal>
            <SectionHead $tight>
              <Eyebrow>FAQ</Eyebrow>
              <H2>Questions, answered.</H2>
            </SectionHead>
          </Reveal>
          <div style={{ maxWidth: 780, margin: "0 auto" }}>
            {faqs.map((item) => (
              <FaqItem
                key={item.question}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </div>
        </Container>
      </Section>

      <section id="cta">
        <Container>
          <Reveal>
            <CTAPanel>
              <div
                style={{
                  position: "absolute",
                  right: -140,
                  top: -140,
                  width: 460,
                  height: 460,
                  borderRadius: "50%",
                  background:
                    `radial-gradient(circle, color-mix(in srgb, ${C.orange} 42%, transparent), transparent 70%)`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: -160,
                  bottom: -180,
                  width: 460,
                  height: 460,
                  borderRadius: "50%",
                  background:
                    `radial-gradient(circle, color-mix(in srgb, ${C.blue} 55%, transparent), transparent 70%)`,
                }}
              />
              <h2
                style={{
                  position: "relative",
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: "clamp(31px, 5.6vw, 50px)",
                  lineHeight: 1.06,
                  letterSpacing: "-.024em",
                  color: C.onDark,
                  margin: "0 auto",
                  maxWidth: 640,
                  textWrap: "balance",
                }}
              >
                Your next great hire is already{" "}
                <Serif style={{ color: C.amber }}>out there.</Serif>
              </h2>
              <p
                style={{
                  position: "relative",
                  margin: "20px auto 0",
                  fontSize: "16.5px",
                  lineHeight: 1.55,
                  color: `color-mix(in srgb, ${C.onDark} 70%, transparent)`,
                  maxWidth: 470,
                }}
              >
                The whole market — every role, company, and signal — on one desk.
                Yours.
              </p>
              <CTAButtons>
                <OutlineDarkLink as="button" type="button" onClick={() => setDemoOpen(true)}>
                  Book a demo
                </OutlineDarkLink>
              </CTAButtons>
            </CTAPanel>
          </Reveal>
        </Container>
      </section>

      <MarketingFooter onDemo={() => setDemoOpen(true)} />
    </Page>
  );
}

/* Right-hand nav actions every marketing page shows. `onDemo` opens the demo
   form; `links` ([{href, label, current}]) fill the phone menu. */
export function MarketingNavActions({ onDemo, onSignup, onLogin, pending = false, links = [] }) {
  const { login } = useAuth();
  const doLogin = onLogin || (() => login());
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  /* Close on outside tap, Escape, or growing past the phone layout */
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    const onResize = () => window.innerWidth > 960 && setMenuOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <NavActions ref={wrapRef}>
      <TextButton type="button" data-nav-login onClick={doLogin} disabled={pending}>
        Log in
      </TextButton>
      <GhostLink as="button" type="button" data-nav-demo onClick={onDemo}>
        Get a demo
      </GhostLink>
      <PrimaryButton type="button" data-nav-signup onClick={onSignup} disabled={pending}>
        Sign up<span data-nav-extra>&nbsp;for free</span>
      </PrimaryButton>
      <MenuButton
        type="button"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls="marketing-menu"
        onClick={() => setMenuOpen((o) => !o)}
      >
        {menuOpen ? <IconCross size={18} /> : <IconMenu size={18} />}
      </MenuButton>
      {menuOpen && (
        <MobileMenu id="marketing-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              aria-current={l.current ? "page" : undefined}
              onClick={close}
            >
              {l.label}
            </a>
          ))}
          <div data-menu-actions>
            <GhostLink
              as="button"
              type="button"
              onClick={() => {
                close();
                doLogin();
              }}
              disabled={pending}
            >
              Log in
            </GhostLink>
            <GhostLink
              as="button"
              type="button"
              onClick={() => {
                close();
                onDemo();
              }}
            >
              Get a demo
            </GhostLink>
          </div>
        </MobileMenu>
      )}
    </NavActions>
  );
}

/* Site footer, shared by the landing and pricing pages. `onDemo` opens the demo form. */
export function MarketingFooter({ onDemo }) {
  return (
    <footer style={{ padding: "64px 0 48px", borderTop: `1px solid ${C.hair}` }}>
      <Container>
        <FooterGrid>
          <div>
            <img
              src={LOGO}
              alt="scoutm8"
              style={{ height: 30, width: "auto", display: "block" }}
            />
            <p
              style={{
                marginTop: 14,
                fontSize: "13.5px",
                color: C.muted,
                maxWidth: 270,
                lineHeight: 1.55,
              }}
            >
              Recruitment, made simple. Spend more time in conversations and
              less time at your desk.
            </p>
            <div
              style={{
                marginTop: 18,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                maxWidth: 290,
              }}
            >
              <ContactLine>
                <IconPin size={15} stroke={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  Unit 1401-2, Yue Shing Commercial Building
                  <br />
                  15-16 Queen Victoria St, Central, Hong Kong
                </span>
              </ContactLine>
              <ContactLine>
                <IconPhone size={15} stroke={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
                <a href="tel:+85226185000">+852 2618 5000</a>
              </ContactLine>
              <ContactLine>
                <IconMail size={15} stroke={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
                <a href="mailto:hello@scoutm8.com">hello@scoutm8.com</a>
              </ContactLine>
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color: C.muted,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              Product
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <FooterLink href="#features">Features</FooterLink>
              <FooterLink href="#copilot">AI Co-pilot</FooterLink>
              <FooterLink href="#stats">At a glance</FooterLink>
              <FooterLink href="/pricing">Pricing</FooterLink>
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color: C.muted,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              Company
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <FooterLink href="mailto:hello@scoutm8.com">Contact</FooterLink>
              <FooterLink as="button" type="button" onClick={onDemo}>
                Book a demo
              </FooterLink>
              <FooterLink href="/terms">Terms &amp; Conditions</FooterLink>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: ".07em",
                color: C.muted,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              Follow
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <FooterLink
                href="https://www.linkedin.com/company/scoutm8/home/"
                target="_blank"
                rel="noreferrer"
              >
                <IconLinkedIn size={16} />
                LinkedIn
              </FooterLink>
              <FooterLink
                href="https://www.youtube.com/@scoutm8"
                target="_blank"
                rel="noreferrer"
              >
                <IconYouTube size={17} />
                YouTube
              </FooterLink>
            </div>
          </div>
        </FooterGrid>
        <FooterBottom>
          <span>© {new Date().getFullYear()} scoutm8. All rights reserved.</span>
          <span>Made for modern recruiters.</span>
        </FooterBottom>
      </Container>
    </footer>
  );
}

/* Marketing chrome reused by the pricing page so the two pages share one design. */
export {
  LandingGlobalStyle as MarketingGlobalStyle,
  Page as MarketingPage,
  Container,
  Nav,
  NavInner,
  NavLogo,
  NavLinks,
  NavLink,
  PrimaryButton,
  OutlineDarkLink,
  Section,
  Serif,
  H1,
  H2,
  Eyebrow,
  SectionHead,
  CTAPanel,
  CTAButtons,
  FaqItem,
  Reveal,
  DemoModal,
  IconCheck,
  LOGO,
  SANS,
  SERIF,
};
